import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, Loader2, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { primaryButtonClass, secondaryButtonClass, ServerError } from "@/components/Panel";
import { Badge, Card } from "@/components/ui";
import { TotpEnroll } from "@/features/security/Totp";
import { isAal2, mfaRequiredOf, mfaStepOf, useSession } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { useFormat } from "@/lib/format";
import { useCan } from "@/lib/session";
import { isDemoMode, supabase } from "@/lib/supabase";

// Settings → Security (PX-SYS-130, docs/design/security.md):
//   my two-step sign-in  — authenticator apps of the signed-in user (Supabase
//                          Auth MFA, TOTP): add, remove
//   organization policy  — who must use it (tenants.mfa_policy: off | admins |
//                          all), set_mfa_policy: core.tenant.manage + aal2
// Round-trips: listFactors (Auth) + one tenants row; nothing on other screens.

export const Route = createFileRoute("/_app/settings/security")({
  component: SecurityPage,
});

type Policy = "off" | "admins" | "all";
const policies: Policy[] = ["off", "admins", "all"];

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

const securityKeys = { factors: ["security", "factors"] as const, policy: ["security", "policy"] as const };

function SecurityPage() {
  const { t } = useTranslation();
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <header className="flex flex-col gap-0.5">
        <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
          {t("nav.settingsGroup.title")} <span aria-hidden="true">/</span> {t("nav.settingsGroup.security")}
        </nav>
        <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("security.title")}</h1>
      </header>
      {isDemoMode ? <p className="text-[13px] text-ink-muted">{t("security.demo")}</p> : <MyTwoStep />}
      {!isDemoMode && <OrganizationPolicy />}
    </div>
  );
}

function MyTwoStep() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const session = useSession();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const factors = useQuery({
    queryKey: securityKeys.factors,
    queryFn: async () => {
      const { data, error: err } = await db().auth.mfa.listFactors();
      if (err) throw err;
      return data.totp.filter((f) => f.status === "verified");
    },
  });
  const remove = useMutation({
    mutationFn: async (factorId: string) => {
      const { error: err } = await db().auth.mfa.unenroll({ factorId });
      if (err) throw err;
      await db().auth.refreshSession();
    },
    onMutate: () => setError(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: securityKeys.factors }),
    onError: (e) => setError(e instanceof Error ? e.message : t("security.removeFailed")),
  });
  const list = factors.data ?? [];
  // Required by the organization: the last authenticator cannot be removed.
  const required = mfaRequiredOf(session);

  return (
    <Card aria-labelledby="sec-mine" className="flex flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-ink">
          <KeyRound size={18} aria-hidden="true" />
        </span>
        <div className="flex grow flex-col">
          <h2 id="sec-mine" className="text-base font-semibold">
            {t("security.mineTitle")}
          </h2>
          <p className="text-[13px] text-ink-muted">{t("security.mineBody")}</p>
        </div>
        <Badge tone={list.length > 0 ? "success" : "neutral"}>{list.length > 0 ? t("security.on") : t("security.off")}</Badge>
      </div>
      <ul className="flex flex-col divide-y divide-border-subtle rounded-xl border border-border" aria-label={t("security.authenticators")}>
        {factors.isPending ? (
          <li aria-hidden="true" className="h-12 animate-pulse bg-surface-muted" />
        ) : list.length === 0 ? (
          <li className="px-3 py-2.5 text-[13px] text-ink-muted">{t("security.none")}</li>
        ) : (
          list.map((f) => (
            <li key={f.id} className="flex items-center gap-2 px-3 py-2.5 text-[13px]">
              <ShieldCheck size={15} aria-hidden="true" className="text-(--status-success-fg)" />
              <span className="grow">
                {f.friendly_name ?? t("security.authenticator")}
                <span className="ml-1.5 text-xs text-ink-muted">{t("security.added", { when: fmt.dateTime(f.created_at) })}</span>
              </span>
              <button
                type="button"
                disabled={remove.isPending || (required && list.length === 1)}
                title={required && list.length === 1 ? t("security.lastRequired") : undefined}
                onClick={() => remove.mutate(f.id)}
                className={cn(secondaryButtonClass, "h-8 px-2.5 text-[13px]")}
              >
                <Trash2 size={14} aria-hidden="true" />
                {t("security.remove")}
              </button>
            </li>
          ))
        )}
      </ul>
      <ServerError message={error} />
      {adding ? (
        <div className="rounded-xl border border-border p-4">
          <TotpEnroll
            onDone={async () => {
              setAdding(false);
              await queryClient.invalidateQueries({ queryKey: securityKeys.factors });
            }}
          />
          <button type="button" onClick={() => setAdding(false)} className="mt-3 text-[13px] font-medium text-ink-muted hover:underline">
            {t("form.cancel")}
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} className={cn(secondaryButtonClass, "self-start")}>
          <Plus size={15} aria-hidden="true" />
          {list.length === 0 ? t("security.enable") : t("security.addAnother")}
        </button>
      )}
      <p className="text-xs text-ink-muted">{t("security.backupHint")}</p>
      {mfaStepOf(session) === null && !isAal2(session) && list.length > 0 && <p className="text-xs text-ink-muted">{t("security.nextSignIn")}</p>}
    </Card>
  );
}

function OrganizationPolicy() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useSession();
  const canManage = useCan("core.tenant.manage");
  const aal2 = isAal2(session);
  const current = useQuery({
    queryKey: securityKeys.policy,
    queryFn: async () => {
      const { data, error } = await db().from("tenants").select("mfa_policy").maybeSingle();
      if (error) throw error;
      return ((data as { mfa_policy: Policy } | null)?.mfa_policy ?? "admins") as Policy;
    },
  });
  const [choice, setChoice] = useState<Policy | null>(null);
  const [error, setError] = useState<string | null>(null);
  const value = choice ?? current.data ?? "admins";
  const save = useMutation({
    mutationFn: async () => {
      const { error: err } = await db().rpc("set_mfa_policy", { p_policy: value });
      if (err) throw err;
      // The next token carries the new mfa_required claim for this user.
      await db().auth.refreshSession();
    },
    onMutate: () => setError(null),
    onSuccess: async () => {
      setChoice(null);
      await queryClient.invalidateQueries({ queryKey: securityKeys.policy });
    },
    onError: (e) =>
      setError(
        (e as { hint?: string }).hint === "mfa_factor_too_new"
          ? t("security.factorTooNew")
          : e instanceof Error && e.message
            ? e.message
            : t("security.saveFailed"),
      ),
  });
  const editable = canManage && aal2;

  return (
    <Card aria-labelledby="sec-org" className="flex flex-col gap-3 p-4">
      <div className="flex flex-col">
        <h2 id="sec-org" className="text-base font-semibold">
          {t("security.orgTitle")}
        </h2>
        <p className="text-[13px] text-ink-muted">{t("security.orgBody")}</p>
      </div>
      <fieldset className="flex flex-col gap-2" disabled={!editable || current.isPending}>
        <legend className="sr-only">{t("security.orgTitle")}</legend>
        {policies.map((p) => (
          <label
            key={p}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 text-[13px] transition-colors",
              value === p ? "border-accent bg-accent-soft" : "border-border hover:bg-surface-hover",
              !editable && "cursor-default opacity-80",
            )}
          >
            <input type="radio" name="mfa-policy" value={p} checked={value === p} onChange={() => setChoice(p)} className="mt-0.5 accent-(--accent)" />
            <span className="flex flex-col">
              <span className="font-medium">{t(`security.policy.${p}`)}</span>
              <span className="text-xs text-ink-muted">{t(`security.policyHint.${p}`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {!canManage ? (
        <p className="text-xs text-ink-muted">{t("security.orgReadOnly")}</p>
      ) : !aal2 ? (
        <p className="text-xs text-(--status-warning-fg)">{t("security.orgNeedsMfa")}</p>
      ) : (
        <button
          type="button"
          disabled={save.isPending || choice === null || choice === current.data}
          onClick={() => save.mutate()}
          className={cn(primaryButtonClass, "self-start")}
        >
          {save.isPending && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
          {t("security.save")}
        </button>
      )}
      <ServerError message={error} />
    </Card>
  );
}
